import React, { useState, useEffect, useRef, Fragment } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Hexagon } from '@mui/icons-material';

const CarouselComponent = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const timeoutRef = useRef(null);

  const carouselItems = [
    {
      id: 1,
      variant: 'default',
      background: '/Images/background1.png',
      mainTitle: 'Discover Top Prestigious Universities Worldwide',
      description: "Empowering student's college journey with DCP's personalized matches from Top Universities Worldwide",
      topLeft: 'SAT & ACT Tutoring',
      topRight: 'AP Tutoring',
      bottomLeft: 'Project Mentorship',
      bottomRight: 'College Counseling',
      accentColor: theme.palette.primary.main,
      indicatorColor: theme.palette.primary.main,
      textColor: theme.palette.primary.main,
      toplogo: '/Images/dreamcollegepath-logo.png',
      headingColor: theme.palette.secondary.main,
    },
    {
      id: 2,
      variant: 'variant2',
      background: '/Images/background2.png',
      mainTitle: `Student's All-In-One Hub for Academic Growth and College Readiness`,
      description: 'Whether you need college guidance, AP mastery, SAT or ACT improvement, or project building support, our holistic services ensure students stay prepared, motivated, and ahead of the curve.',
      topLeft: 'SAT & ACT Tutoring',
      topRight: 'AP Tutoring',
      bottomLeft: 'Project Mentorship',
      bottomRight: 'College Counseling',
      accentColor: theme.palette.secondary.main,
      indicatorColor: theme.palette.primary.main,
      textColor: theme.palette.primary.main,
      toplogo: '/Images/dreamcollegepath-logo.png',
      headingColor: theme.palette.secondary.main,
    },
    {
      id: 3,
      variant: 'variant3',
      background: '/Images/background3.png',
      mainTitle: 'The Dream College Path Youtube Channel',
      description: 'A full spectrum learning hub featuring test prep breakdowns, AP support, application guidance, project coaching, and interviews with local educators to give students well rounded insights.',
      topLeft: 'Study Help',
      topRight: 'Success Stories',
      bottomLeft: 'Insightful Interviews',
      bottomRight: 'Application Support',
      accentColor: theme.palette.success.main,
      indicatorColor: theme.palette.primary.main,
      textColor: theme.palette.primary.main,
      toplogo: '/Images/youtube.png',
      headingColor: theme.palette.secondary.main,
    },
    {
      id: 4,
      variant: 'variant4',
      background: '/Images/background3.png',
      mainTitle: 'Your Dream College Planner.',
      description: 'Navigate your admissions journey with expert counseling, targeted SAT/ACT prep, and standout project mentorship - all in one place.',
      topLeft: 'Admission Trends',
      topRight: 'Proven Strategies',
      bottomLeft: 'Test Strateigies',
      bottomRight: 'Career Exploration',
      accentColor: theme.palette.warning.main,
      indicatorColor: theme.palette.primary.main,
      textColor: theme.palette.primary.main,
      toplogo: '/Images/blogs.png',
      headingColor: theme.palette.secondary.main,
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setPrevIndex(activeIndex);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % carouselItems.length);
        setIsAnimating(false);
      }, 800); // Reduced to 800ms for faster transitions
    }, 1400); //

    return () => clearInterval(interval);
  }, [activeIndex, carouselItems.length]);

  const handleSlideChange = (index) => {
    if (index === activeIndex || isAnimating) return;

    setIsAnimating(true);
    setPrevIndex(activeIndex);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setActiveIndex(index);
      setIsAnimating(false);
    }, 1400); // Reduced to 800ms for faster transitions
  };

  const currentSlide = carouselItems[activeIndex];
  const previousSlide = carouselItems[prevIndex];
  const isBlogSlide = currentSlide.variant === 'variant4';
  const isFirstSlide = activeIndex === 0;
  const isSecondSlide = activeIndex === 1;
  const isThirdSlide = activeIndex === 2;
  const wasThirdSlide = prevIndex === 2;
  const isMovingFromThirdSlide = wasThirdSlide && isAnimating;

  // Determine if we should show exit animation for third slide
  const shouldShowExitAnimation = isMovingFromThirdSlide;

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Images with Smooth Slide Animation */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          backgroundImage: `url(${carouselItems[activeIndex].background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />

      {currentSlide.variant !== 'default' ? (
        <Fragment>
          <Box
            sx={{
              position: 'absolute',
              top: 15,
              left: 30,
              zIndex: 20,
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Hexagon sx={{ color: theme.palette.primary.main, fontSize: 140 }} />
              <Typography
                variant={isMobile ? "body1" : "h6"}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontWeight: 600,
                  color: currentSlide.headingColor,
                  fontSize: '13px',
                  textAlign: 'center',
                  opacity: 1,
                  transition: 'opacity 0.5s ease-in-out',
                  zIndex: 1,
                }}
              >
                {currentSlide.topLeft}
              </Typography>
            </Box>
          </Box>

          {/* Top Right Corner Content */}
          <Box
            sx={{
              position: 'absolute',
              top: 15,
              right: 30,
              zIndex: 20,
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Hexagon sx={{ color: '#72CCFD', fontSize: 140 }} />
              <Typography
                variant={isMobile ? "body1" : "h6"}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontWeight: 600,
                  color: currentSlide.headingColor,
                  fontSize: '13px',
                  textAlign: 'center',
                  opacity: 1,
                  transition: 'opacity 0.5s ease-in-out',
                  zIndex: 1,
                }}
              >
                {currentSlide.topRight}
              </Typography>
            </Box>
          </Box>

          {/* Bottom Left Corner Content */}
          {currentSlide.bottomLeft && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 15,
                left: 30,
                zIndex: 20,
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Hexagon sx={{ color: '#FFFFFF', fontSize: 140 }} />
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontWeight: 600,
                    color: currentSlide.headingColor,
                    fontSize: '13px',
                    textAlign: 'center',
                    opacity: 1,
                    transition: 'opacity 0.5s ease-in-out',
                    zIndex: 1,
                  }}
                >
                  {currentSlide.bottomLeft}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Bottom Right Corner Content */}
          {currentSlide.bottomRight && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 15,
                right: 30,
                zIndex: 20,
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Hexagon sx={{ color: '#96CDF8', fontSize: 140 }} />
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontWeight: 600,
                    color: currentSlide.headingColor,
                    fontSize: '13px',
                    textAlign: 'center',
                    opacity: 1,
                    transition: 'opacity 0.5s ease-in-out',
                    zIndex: 1,
                  }}
                >
                  {currentSlide.bottomRight}
                </Typography>
              </Box>
            </Box>
          )}
        </Fragment>
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: '12%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 20,
            display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on desktop
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'nowrap',
            width: 'auto',
            maxWidth: '90%',
          }}
        >
          {[
            { color: theme.palette.primary.main, text: currentSlide.topLeft },
            { color: '#72CCFD', text: currentSlide.topRight },
            { color: '#FFFFFF', text: currentSlide.bottomLeft },
            { color: '#96CDF8', text: currentSlide.bottomRight },
          ].map((item, index, arr) => (
            <React.Fragment key={index}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Hexagon sx={{ color: item.color, fontSize: 120 }} />
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontWeight: 600,
                    color: currentSlide.headingColor,
                    fontSize: '12px',
                    textAlign: 'center',
                    zIndex: 1,
                    width: '70px',
                  }}
                >
                  {item.text}
                </Typography>
              </Box>

              {/* Connector */}
              {index < arr.length - 1 && (
                <Box
                  sx={{
                    position: 'relative',
                    mx: '-16px',
                    mb: index === 1 ? '-45px' : '35px',
                    flexShrink: 0,
                    zIndex: 0,
                  }}
                >
                  <svg
                    width="40"
                    height="20"
                    viewBox="0 0 65 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d={index === 1
                        ? "M 0 5 Q 32 12 65 5"
                        : "M 0 15 Q 32 8 65 15"
                      }
                      stroke="rgba(220,225,230,0.9)"
                      strokeWidth="2"
                      strokeDasharray="20 5"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 6, md: 8 },
          pb: { xs: 8, md: 10 },
          position: 'relative',
          zIndex: 10,
        }}
      >
        {/* Exit animation when moving FROM 3rd slide - Reduced duration */}
        {shouldShowExitAnimation ? (
          <>
            {/* Logo/Image with faster fade out animation */}
            <Box
              component="img"
              src={previousSlide.toplogo}
              alt='logo'
              sx={{
                width: '100px',
                height: previousSlide.variant === 'variant4' ? 'auto' : '100px',
                animation: 'quickFadeOut 0.6s ease-out forwards',
                '@keyframes quickFadeOut': {
                  '0%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                  '100%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                },
              }}
            />

            {/* Main Title with faster fade out animation */}
            <Typography
              variant={isMobile ? "h6" : "h4"}
              sx={{
                fontWeight: 700,
                color: previousSlide.textColor,
                textAlign: 'center',
                maxWidth: previousSlide.variant === 'variant4' ? '50%' : currentSlide.variant === 'variant2' ? '80%' : '70%',
                fontSize: '30px',
                mt: 3,
                animation: 'quickFadeOut 0.6s ease-out 0.1s forwards',
                '@keyframes quickFadeOut': {
                  '0%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                  '100%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                },
              }}
            >
              {previousSlide.mainTitle}
            </Typography>

            {/* Description with faster fade out animation */}
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                maxWidth: { xs: '95%', md: '80%' },
                color: theme.palette.common.white,
                fontSize: '18px',
                fontWeight: 500,
                px: { xs: 1, md: 2 },
                padding: { xs: 2, md: 3 },
                animation: 'quickFadeOut 0.6s ease-out 0.2s forwards',
                '@keyframes quickFadeOut': {
                  '0%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                  '100%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                },
              }}
            >
              {previousSlide.description}
            </Typography>
          </>
        ) : (
          <>
            <Box
              component="img"
              src={currentSlide.toplogo}
              alt='logo'
              sx={{
                width: isBlogSlide ? '30%' : '100px',
                height: isBlogSlide ? 'auto' : '100px',
                // Animation for first and third slides only
                ...((isFirstSlide || isThirdSlide) && {
                  animation: 'fadeInBottom 0.8s ease-out',
                  '@keyframes fadeInBottom': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(100px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }),
                // No animation for second and fourth slides, just smooth transition
                ...((isSecondSlide || activeIndex === 3) && {
                  opacity: 1,
                  transition: 'opacity 0.5s ease-in-out',
                }),
              }}
            />

            {/* Main Title */}
            <Typography
              variant={isMobile ? "h6" : "h4"}
              sx={{
                fontWeight: 700,
                color: currentSlide.textColor,
                textAlign: 'center',
                maxWidth: isBlogSlide ? '50%' : '70%',
                fontSize: '30px',
                mt: 3,
                // Animation for first and third slides only
                ...((isFirstSlide || isThirdSlide) && {
                  animation: 'fadeInBottom 0.8s ease-out 0.2s both',
                  '@keyframes fadeInBottom': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(100px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }),
                // No animation for second and fourth slides, just smooth transition
                ...((isSecondSlide || activeIndex === 3) && {
                  opacity: 1,
                  transition: 'opacity 0.5s ease-in-out',
                }),
              }}
            >
              {currentSlide.mainTitle}
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                maxWidth: { xs: '95%', md: '80%' },
                color: theme.palette.common.white,
                fontSize: '18px',
                fontWeight: 500,
                px: { xs: 1, md: 2 },
                padding: { xs: 2, md: 3 },
                // Animation for first and third slides only
                ...((isFirstSlide || isThirdSlide) && {
                  animation: 'fadeInBottom 0.8s ease-out 0.4s both',
                  '@keyframes fadeInBottom': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(100px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }),
                // No animation for second and fourth slides, just smooth transition
                ...((isSecondSlide || activeIndex === 3) && {
                  opacity: 1,
                  transition: 'opacity 0.5s ease-in-out',
                }),
              }}
            >
              {currentSlide.description}
            </Typography>
          </>
        )}
      </Box>

      {/* Indicators with Sliding Animation */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 35,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 20,
          px: 3,
          py: 1.5,
        }}
      >
        {/* Container for dots with relative positioning */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Active indicator background that slides */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: currentSlide.indicatorColor,
              transform: `translateX(${activeIndex * 28}px)`,
              transition: 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
              zIndex: 1
            }}
          />

          {/* Indicator dots */}
          {carouselItems.map((_, index) => {
            const isActive = index === activeIndex;

            return (
              <Box
                key={index}
                onClick={() => handleSlideChange(index)}
                sx={{
                  position: 'relative',
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                  border: `2px solid ${isActive ? 'transparent' : 'rgba(255, 255, 255, 0.7)'}`,
                  cursor: isAnimating ? 'not-allowed' : 'pointer',
                  zIndex: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: isAnimating ? 'transparent' : `${currentSlide.indicatorColor}40`,
                    transform: isAnimating ? 'scale(1)' : 'scale(1.2)',
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CarouselComponent;